'use client'
import React, { useState, useEffect, useRef } from 'react'
import LargePropertyCard from '@components/LargePropertyCard'
import LoadingCard from '@components/LoadingCard'
import { company } from '@constants/company'
import { getPanel } from '@services/getPanel'
import LinkComponent from './LinkComponent'
import { MdOutlineKeyboardDoubleArrowRight } from "react-icons/md";
import tailwindConfig from '@tailwind.config'
import LandingPageDescription from '@components/LandingPageDescription'
import WebFilterTabSearchPage from '@components/Search/WebFilter/WebFilterTabSearchPage'
import WebFilterTabMobileSearchPage from '@components/Search/WebFilter/WebFilterTabMobileSearchPage'

const SearchPanelCards = ({
    slug,
    title,
    description,
    districtList,
    searchParams,
    dictionary,
    lang = 'en',
    initialProperties = [],
    initialCount = 0,
    initialPages = 1,
}) => {
    let userEmail = null

    const [isLoading, setIsLoading] = useState(false)
    const [search, setSearch] = useState(searchParams)
    const [page, setPage] = useState(1);
    const [allPages, setAllPages] = useState(initialPages);
    const [properties, setProperties] = useState(initialProperties);
    const [panelQuery, setPanelQuery] = useState({})
    const [propertyCount, setPropertyCount] = useState(initialCount);
    const [coordinates, setCoordinates] = useState([])
    const [hoveredProperty, setHoveredProperty] = useState(null)
    const [activeProperty, setActiveProperty] = useState(
        initialProperties?.[0]?.id ?? {}
    )
    const [activePropertyLat, setActivePropertyLat] = useState(
        initialProperties?.[0]?.latitude ? Number(initialProperties[0].latitude) : 13.732772
    )
    const [activePropertyLng, setActivePropertyLng] = useState(
        initialProperties?.[0]?.longitude ? Number(initialProperties[0].longitude) : 100.5801953
    )

    // Track whether the initial SSR data has already been used,
    // so we don't re-fetch on the very first mount unnecessarily.
    const isFirstMount = useRef(true)

    const handleMouseEnter = (propertyId) => {
        setHoveredProperty(propertyId)
    };
    const handleMouseLeave = () => {
        setHoveredProperty(null)
    };

    const handleSlideChange = (swiper) => {
        const activeSlideIndex = swiper.activeIndex;
        let activePropertyId = swiper.slides[activeSlideIndex].id
        let activePropertyLat = swiper.slides[activeSlideIndex].dataset.latitude
        let activePropertyLng = swiper.slides[activeSlideIndex].dataset.longitude
        setActiveProperty(activePropertyId)
        setActivePropertyLat(activePropertyLat)
        setActivePropertyLng(activePropertyLng)
    };

    const getData = async (pageNumber) => {
        setIsLoading(true)

        const result = await getPanel({ slug, localeValue: lang, limit: 8, page: pageNumber })
        setPanelQuery(result.panel.query)

        if (pageNumber > 1) {
            setProperties((prev) => [...prev, ...result.properties])
        } else {
            setProperties(result.properties)
        }

        setAllPages(result.pages)
        setPropertyCount(result.count)
        setCoordinates(result.coordinates)

        if (result.properties.length > 0 && pageNumber === 1) {
            setActiveProperty(result.properties[0].id)
            setActivePropertyLat(Number(result.properties[0].latitude))
            setActivePropertyLng(Number(result.properties[0].longitude))
        }

        setIsLoading(false)
    }

    // Re-fetch when searchParams change, but skip the very first mount
    // if we already have SSR-provided initialProperties.
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false
            // If we already have SSR data, skip the redundant fetch on mount.
            if (initialProperties.length > 0) return
        }

        setPage(1)
        setProperties([])
        getData(1)
    }, [searchParams])

    // Load more pages when page increments past 1
    useEffect(() => {
        if (page === 1) return
        getData(page)
    }, [page])

    return (
        <div className='md:w-full 2xl:w-10/12 max-w-[1600px] flex md:flex-row flex-col flex-nowrap md:flex-wrap mx-auto px-0'>
            <div className='w-full md:block hidden'>
                <WebFilterTabSearchPage lang={lang} dictionary={dictionary.filter} searchParams={searchParams} districtList={districtList} />
            </div>
            <div className='w-full md:hidden block'>
                <WebFilterTabMobileSearchPage lang={lang} dictionary={dictionary.filter} searchParams={searchParams} districtList={districtList} />
            </div>
            <div className='mt-2 sm:px-0 px-2'>
                <ul className='flex flex-row items-center my-2'>
                    <li className='flex flex-row items-center'>
                        <LinkComponent href={`/`} className={'text-maincolor hover:underline hover:underline-offset-2'}>
                            {dictionary.search.home}
                        </LinkComponent>
                        <MdOutlineKeyboardDoubleArrowRight
                            color={tailwindConfig.theme.extend.colors.secondary}
                            className='mx-1'
                        />
                    </li>
                    <li className='flex flex-row items-center'>
                        <LinkComponent href={`/buy`} className={'text-maincolor hover:underline hover:underline-offset-2'}>
                            {dictionary.search?.search}
                        </LinkComponent>
                        <MdOutlineKeyboardDoubleArrowRight
                            color={tailwindConfig.theme.extend.colors.secondary}
                            className='mx-1'
                        />
                    </li>
                    <li>{title}</li>
                </ul>
                <div className='my-2'>
                    <h1 className='text-3xl font-semibold mb-2'>{title}</h1>
                    {description && (
                        <LandingPageDescription html={description} dictionary={dictionary} />
                    )}
                </div>
            </div>
            <div className='w-full min-h-[70vh] flex flex-row flex-wrap'>
                {properties.map((property, index) => (
                    <div
                        key={property.id}
                        className='m-2'
                    >
                        <LargePropertyCard
                            saleType={searchParams?.name?.toLowerCase()?.startsWith(company?.refStarter) ? '' : searchParams?.saleType}
                            dictionary={dictionary.card}
                            lang={lang}
                            property={property}
                            isLast={index === properties.length - 1}
                            imageWidth={320}
                            newLimit={() => { page < allPages ? setPage(page + 1) : setPage(page) }}
                        />
                    </div>
                ))}
                {isLoading && (
                    <>
                        <div className='m-2'><LoadingCard /></div>
                        <div className='m-2'><LoadingCard /></div>
                        <div className='m-2'><LoadingCard /></div>
                        <div className='m-2'><LoadingCard /></div>
                        <div className='m-2'><LoadingCard /></div>
                    </>
                )}
                {!isLoading && properties.length === 0 && (
                    <div className='w-full h-full flex justify-center items-center'>
                        <div className='flex flex-col gap-4 items-center'>
                            <p className='text-lg'>No listings currently available</p>
                            <LinkComponent href={'/properties'}>
                                <button className='bg-maincolor text-white rounded-md px-4 py-2'>Go to Search Page</button>
                            </LinkComponent>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SearchPanelCards